import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { WorkbiePayoutsService } from '../services/workbie-payouts.service';
import { WorkbiePayoutDetailDTO } from '../model/workbie-payout-detail-dto';
import { ProcessPaymentRequestDTO, SupportFileDTO } from '../model/process-payment-dto';
import {
  WorkbiePayoutsSummaryDTO,
  WorkbiePendingItemDTO,
  WorkbiePaidItemDTO
} from '../model/workbie-payout-dashboard-dto';

const OVERDUE_DAYS_THRESHOLD = 7;

@Component({
  selector: 'app-workbie-payouts',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTableModule,
    MatInputModule,
    MatFormFieldModule,
    MatSnackBarModule,
    MatTabsModule,
    MatDatepickerModule,
    MatExpansionModule,
    MatTooltipModule
  ],
  templateUrl: './workbie-payouts.component.html',
  styleUrls: ['./workbie-payouts.component.scss']
})
export class WorkbiePayoutsComponent implements OnInit {

  // ---- Tarjetas de resumen ----
  summary: WorkbiePayoutsSummaryDTO | null = null;
  loadingSummary: boolean = false;
  errorSummary: string | null = null;

  // ---- Tabs ----
  selectedTabIndex: number = 0;
  private pendingLoaded: boolean = false;
  private paidLoaded: boolean = false;

  // ---- Pestaña Pendientes ----
  displayedPendingColumns: string[] = ['workbieInfo', 'jobCount', 'totalAmount', 'pendingSince', 'actions'];
  pendingList: WorkbiePendingItemDTO[] = [];
  loadingPending: boolean = false;
  errorPending: string | null = null;
  pendingPage: number = 0; // 0-based
  pendingPageSize: number = 10;
  pendingTotalElements: number = 0;

  // ---- Pestaña Pagados ----
  displayedPaidColumns: string[] = ['workbieInfo', 'originalAmount', 'totalAmount', 'paidAt', 'provider'];
  paidList: WorkbiePaidItemDTO[] = [];
  loadingPaid: boolean = false;
  errorPaid: string | null = null;
  paidPage: number = 0; // 0-based
  paidPageSize: number = 10;
  paidTotalElements: number = 0;
  filterFrom: Date | null = null;
  filterTo: Date | null = null;

  // ---- Detalle seleccionado (pagos pendientes de un workbie) ----
  selectedDetail: WorkbiePayoutDetailDTO | null = null;
  loadingDetail: boolean = false;
  error: string | null = null;

  // ---- Formulario de pago ----
  paymentAmount: number = 0;
  paymentObservation: string = '';
  selectedFiles: File[] = [];
  processingPayment: boolean = false;

  constructor(
    private workbiePayoutsService: WorkbiePayoutsService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadSummary();
    this.loadPending();
  }

  // ==================== Resumen ====================

  loadSummary(): void {
    this.loadingSummary = true;
    this.errorSummary = null;

    this.workbiePayoutsService.getSummary().subscribe({
      next: (response) => {
        if (response.statusCode === '000' && response.data) {
          this.summary = response.data;
        } else {
          this.errorSummary = response.message || 'Error al cargar el resumen de pagos';
        }
        this.loadingSummary = false;
      },
      error: (err) => {
        this.errorSummary = 'Error al cargar el resumen: ' + err.message;
        this.loadingSummary = false;
      }
    });
  }

  // ==================== Tabs ====================

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
    if (index === 0 && !this.pendingLoaded) {
      this.loadPending();
    } else if (index === 1 && !this.paidLoaded) {
      this.loadPaid();
    }
  }

  // ==================== Pestaña Pendientes ====================

  loadPending(): void {
    this.loadingPending = true;
    this.errorPending = null;

    // /pending es 0-based: pendingPage ya está en ese formato, se envía tal cual
    this.workbiePayoutsService.getPending(this.pendingPage, this.pendingPageSize)
      .subscribe({
        next: (response) => {
          if (response.statusCode === '000' && response.data) {
            this.pendingList = response.data.list;
            this.pendingTotalElements = response.data.totalElements;
            this.pendingLoaded = true;
          } else {
            this.errorPending = response.message || 'Error al cargar los pagos pendientes';
          }
          this.loadingPending = false;
        },
        error: (err) => {
          this.errorPending = 'Error al cargar los pagos pendientes: ' + err.message;
          this.loadingPending = false;
        }
      });
  }

  onPendingPageChange(event: PageEvent): void {
    this.pendingPage = event.pageIndex;
    this.pendingPageSize = event.pageSize;
    this.loadPending();
  }

  isOverdue(oldestCreatedAt: string): boolean {
    return this.daysSince(oldestCreatedAt) > OVERDUE_DAYS_THRESHOLD;
  }

  private daysSince(dateString: string): number {
    const diffMs = Date.now() - new Date(dateString).getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  }

  // ==================== Pestaña Pagados ====================

  loadPaid(): void {
    this.loadingPaid = true;
    this.errorPaid = null;

    const from = this.filterFrom ? this.formatDateForApi(this.filterFrom) : undefined;
    const to = this.filterTo ? this.formatDateForApi(this.filterTo) : undefined;

    // /paid es 0-based: paidPage ya está en ese formato, se envía tal cual
    this.workbiePayoutsService.getPaid(this.paidPage, this.paidPageSize, from, to)
      .subscribe({
        next: (response) => {
          if (response.statusCode === '000' && response.data) {
            this.paidList = response.data.list;
            this.paidTotalElements = response.data.totalElements;
            this.paidLoaded = true;
          } else {
            this.errorPaid = response.message || 'Error al cargar los pagos realizados';
          }
          this.loadingPaid = false;
        },
        error: (err) => {
          this.errorPaid = 'Error al cargar los pagos realizados: ' + err.message;
          this.loadingPaid = false;
        }
      });
  }

  onPaidPageChange(event: PageEvent): void {
    this.paidPage = event.pageIndex;
    this.paidPageSize = event.pageSize;
    this.loadPaid();
  }

  applyPaidFilter(): void {
    this.paidPage = 0;
    this.loadPaid();
  }

  clearPaidFilter(): void {
    this.filterFrom = null;
    this.filterTo = null;
    this.paidPage = 0;
    this.loadPaid();
  }

  hasPaidFilter(): boolean {
    return !!this.filterFrom || !!this.filterTo;
  }

  /** Convierte un Date del datepicker a 'yyyy-MM-dd' en hora local (evita corrimientos por UTC) */
  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // ==================== Detalle de pagos pendientes de un workbie ====================

  viewPayoutDetails(workbieUserId: string): void {
    this.loadingDetail = true;
    this.selectedDetail = null;
    this.error = null;
    this.resetPaymentForm();

    this.workbiePayoutsService.getPendingDetail(workbieUserId)
      .subscribe({
        next: (response) => {
          if (response.statusCode === '000' && response.data) {
            this.selectedDetail = response.data;
            this.paymentAmount = response.data.totalAmountToPay;
          } else {
            this.error = response.message || 'Error al cargar los detalles del pago';
          }
          this.loadingDetail = false;
        },
        error: (err) => {
          this.error = 'Error al cargar los detalles: ' + err.message;
          this.loadingDetail = false;
        }
      });
  }

  closeDetailModal(): void {
    this.selectedDetail = null;
    this.resetPaymentForm();
  }

  resetPaymentForm(): void {
    this.paymentAmount = 0;
    this.paymentObservation = '';
    this.selectedFiles = [];
  }

  onFilesSelected(event: any): void {
    const files: FileList = event.target.files;
    this.selectedFiles = Array.from(files);
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  isPaymentValid(): boolean {
    if (!this.selectedDetail) return false;
    if (this.selectedFiles.length === 0) return false;
    if (this.paymentAmount <= 0) return false;
    if (this.paymentAmount > this.selectedDetail.totalAmountToPay) return false;
    return true;
  }

  async processPayment(): Promise<void> {
    if (!this.selectedDetail || !this.isPaymentValid()) {
      this.snackBar.open('Por favor complete todos los campos requeridos correctamente', 'Cerrar', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.processingPayment = true;
    this.error = null;

    try {
      // Convertir archivos a base64
      const supportFiles: SupportFileDTO[] = await Promise.all(
        this.selectedFiles.map(async (file) => ({
          fileName: file.name,
          extension: file.name.split('.').pop() || 'pdf',
          contentType: file.type || 'application/pdf',
          base64Content: await this.fileToBase64(file)
        }))
      );

      // Extraer IDs de los payouts
      const payoutIds = this.selectedDetail.jobs.map(job => job.idPayout);

      const request: ProcessPaymentRequestDTO = {
        workbieUserId: this.selectedDetail.workbieUserId,
        payoutIds: payoutIds,
        totalAmountPaid: this.paymentAmount,
        observation: this.paymentObservation || undefined,
        supportFiles: supportFiles
      };

      this.workbiePayoutsService.processPayment(request)
        .subscribe({
          next: (response) => {
            if (response.statusCode === '000' && response.data) {
              this.snackBar.open(
                `Pago procesado exitosamente. ${response.data.payoutsUpdated} pago(s) actualizado(s).`,
                'Cerrar',
                {
                  duration: 5000,
                  panelClass: ['success-snackbar']
                }
              );
              this.closeDetailModal();
              // Recargar la lista de pendientes y el resumen (ambos cambiaron)
              this.loadPending();
              this.loadSummary();
              // Si la pestaña de pagados ya se había cargado, refrescarla también
              if (this.paidLoaded) {
                this.loadPaid();
              }
            } else {
              this.error = response.message || 'Error al procesar el pago';
            }
            this.processingPayment = false;
          },
          error: (err) => {
            this.error = 'Error al procesar el pago: ' + err.message;
            this.processingPayment = false;
            this.snackBar.open('Error al procesar el pago', 'Cerrar', {
              duration: 3000,
              panelClass: ['error-snackbar']
            });
          }
        });
    } catch (error: any) {
      this.error = 'Error al convertir archivos: ' + error.message;
      this.processingPayment = false;
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remover el prefijo "data:application/pdf;base64," o similar
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  }

  openBankCertification(url: string | null): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

  // ==================== Formateo ====================

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  }

  /** Formatea un monto con signo explícito (+/-), usado en los desgloses de conceptos */
  formatSignedCurrency(amount: number): string {
    const formatted = this.formatCurrency(Math.abs(amount));
    return amount < 0 ? `-${formatted}` : `+${formatted}`;
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatDateOnly(dateString: string): string {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }
}
